/**
 * DÍA 5 — Weakest Area / Mixed.
 *
 * CAMBIO PEDAGÓGICO 3 (aprobado). v2_2 abría el Día 5 preguntando en frío «¿qué
 * te costó más?», y su prompt hacía que ChatGPT lo volviera a preguntar. Eso
 * contradice la spec maestra, que exige que la APLICACIÓN calcule la sugerencia
 * y la explique con sus razones, y que el usuario la acepte o la cambie.
 *
 * Ahora: el paso 1 presenta el foco sugerido CON SUS RAZONES, y el prompt
 * recibe el foco ya decidido.
 *
 * Si no hay datos suficientes, la aplicación LO DICE y ofrece elegir entre las
 * 7 áreas. Nunca inventa una recomendación.
 *
 * `areasAEvaluar` lleva las dos áreas que siempre aplican porque toda sesión
 * termina en transferencia; la aplicación AÑADE en tiempo de ejecución el área
 * del foco elegido, que varía por definición.
 */

import type { Area } from '../../../domain/modelo/areas.js';
import type { ClaveHerramienta, Sesion } from '../../tipos.js';

/**
 * Qué herramienta abre cada foco.
 *
 * Es la capa LIGERA aprobada: un mapeo, no siete currículos. El paso sigue
 * siendo el mismo; lo único que cambia es qué se abre.
 *
 * No se muestra ninguna herramienta que el foco de hoy no use.
 */
export const HERRAMIENTA_DE_FOCO: Record<Area, ClaveHerramienta> = {
  /**
   * Listening usa YouGlish, NO la muestra auténtica.
   *
   * `CURRICULUM.md` es explícito: la muestra auténtica es «para MEDIR», y se
   * usa solo en el baseline y en los checkpoints; YouGlish es la herramienta
   * de targets concretos del Día 2 «y del Día 5 si el foco es
   * listening/naturalidad». El Día 5 entrena, no mide.
   */
  listening: 'youglish',
  naturalidad: 'youglish',
  pronunciacion_prosodia: 'accentCoach',
  recuperacion_lexica: 'chatgpt',
  habla_espontanea: 'chatgpt',
  gramatica_fluidez: 'chatgpt',
  interaccion_turnos: 'chatgpt',
};

/** El foco trabaja material del Learning Bank en vez de conversación libre. */
export const FOCOS_CON_MATERIAL: Area[] = ['recuperacion_lexica', 'naturalidad'];

/**
 * NO añadir aquí un segundo mapa de foco → herramienta.
 *
 * Existió uno (`HERRAMIENTA_SEGUN_FOCO`, con las mismas claves y descripciones
 * en prosa) que nadie consumía y que además decía otra cosa que el de arriba:
 * mandaba listening a YouGlish cuando el vivo lo mandaba a la muestra
 * auténtica. Dos mapas que se contradicen son peores que uno equivocado,
 * porque cada lector cree estar mirando el bueno.
 */

export const D5: Sesion = {
  clave: 'd5',
  tipo: 'dia',
  etiqueta: 'Día 5 · Punto débil',
  titulo: 'Día 5 · Atacar la dificultad principal',
  corto: 'Débil',
  objetivo:
    'Dedicar tiempo adicional al problema que realmente apareció esta semana.',
  porQue:
    'Una distribución fija sería menos eficiente que adaptar parte del volumen a la debilidad que estás observando de verdad. El foco sale de tus propios registros, no de una intuición.',
  areasAEvaluar: ['habla_espontanea', 'naturalidad'],
  capturaCorrecciones: true,
  planes: {
    20: {
      duracion: 20,
      pasos: [
        {
          clave: 'd5-foco',
          titulo: 'Confirmar el foco de hoy',
          minutosSugeridos: 3,
          herramienta: 'ninguna',
          objetivo: 'Fijar una sola prioridad para la sesión.',
          eligeFoco: true,
          porQue:
            'Foco vale más que repartir la atención entre seis debilidades. La aplicación ya calculó una sugerencia con tus datos de esta semana; vos decidís si la aceptás.',
          instrucciones: [
            'Leé el foco sugerido y las razones concretas que lo respaldan.',
            'Si estás de acuerdo, aceptalo. Si no, elegí otra de las 7 áreas.',
            'Si la aplicación te dice que todavía no hay datos suficientes, elegí vos: es preferible a una recomendación inventada.',
            'Una sola prioridad. No cinco.',
          ],
        },
        {
          clave: 'd5-bloque',
          titulo: 'Bloque dirigido',
          minutosSugeridos: 13,
          herramienta: 'segunFoco',
          prompt: 'd5',
          grabadora: true,
          objetivo: 'Trabajar esa prioridad y terminar produciendo.',
          porQue:
            'La práctica específica debe acabar en transferencia. Un ejercicio que no termina en producción propia no se traslada a la conversación.',
          instrucciones: [
            'Usá solo la herramienta que corresponde a tu foco de hoy.',
            'Listening o naturalidad: YouGlish, con un target concreto.',
            'Pronunciación: Accent Coach para el oído, y la grabadora para comparar tu producción.',
            'Habla espontánea, recuperación léxica, gramática o interacción: ChatGPT Voice con el prompt.',
            'Sea cual sea, el bloque termina produciendo vos, no escuchando.',
          ],
          uso: {
            queHacer:
              'Abrí ÚNICAMENTE la herramienta que corresponde a tu foco de hoy y trabajá esa área. Cerrá el bloque produciendo, no solo recibiendo.',
            // Sin esto la interfaz NUNCA pinta el fallback de YouGlish:
            // declarar el texto a secas no lo hace aparecer.
            requiereTarget: true,
            fallbackSinTarget:
              'Todavía no tenés un target concreto. Buscá una palabra o frase que hayas fallado esta semana; si no se te ocurre ninguna, mirá el Learning Bank antes de abrir la herramienta.',
          },
          areas: ['habla_espontanea'],
        },
        {
          clave: 'd5-transferencia',
          titulo: 'Prueba en contexto nuevo',
          minutosSugeridos: 4,
          herramienta: 'chatgpt',
          prompt: 'd5',
          objetivo: 'Usar lo trabajado fuera del ejercicio.',
          porQue:
            'Un bloque que termina dentro de su propia herramienta no demuestra nada: Accent Coach entrena percepción y YouGlish da ejemplos, pero ninguno de los dos te hace producir bajo presión. La transferencia es lo que convierte la práctica en habla.',
          instrucciones: [
            'Pedí una situación distinta de la que practicaste.',
            'No avises de qué estuviste trabajando: dejá que aparezca solo.',
            'Cuatro minutos alcanzan. Es una prueba, no otra sesión.',
          ],
          uso: {
            queHacer:
              'En ChatGPT Voice, conversá en una situación nueva y comprobá si lo trabajado aparece sin que lo fuerces.',
          },
          areas: ['habla_espontanea', 'naturalidad'],
        },
      ],
    },

    35: {
      duracion: 35,
      pasos: [
        {
          clave: 'd5-foco',
          titulo: 'Confirmar el foco de hoy',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          objetivo: 'Basar la sesión en los datos de la semana, no en la intuición.',
          eligeFoco: true,
          porQue:
            'Ya tenés errores reales registrados y autoevaluaciones. No hace falta adivinar qué entrenar.',
          instrucciones: [
            'Leé el foco sugerido y sus razones.',
            'Revisá el Learning Bank y las sesiones de esta semana si querés contrastar.',
            'Aceptá el foco o elegí otro.',
            'Si no hay datos suficientes, la aplicación te lo dirá: elegí vos.',
          ],
        },
        {
          clave: 'd5-entrenamiento',
          titulo: 'Entrenamiento dirigido',
          minutosSugeridos: 25,
          herramienta: 'segunFoco',
          prompt: 'd5',
          grabadora: true,
          objetivo: 'Concentrar suficiente práctica en una sola área.',
          porQue:
            'Una sesión flexible permite corregir donde más retorno esperás, en vez de repartir el tiempo por igual entre cosas que ya funcionan.',
          instrucciones: [
            'Usá la herramienta que corresponda a tu foco.',
            'Evitá ejercicios repetitivos que no terminen en transferencia.',
            'Si el foco es pronunciación, grabate al menos una vez y escuchate.',
            'Si el foco es listening o naturalidad, YouGlish necesita un target concreto: una palabra o una frase exacta, nunca un tema.',
          ],
          uso: {
            queHacer:
              'Abrí solo la herramienta de tu foco y trabajá esa área con suficiente volumen. Nada de drills repetitivos sin producción.',
            // Sin esto la interfaz NUNCA pinta el fallback de YouGlish:
            // declarar el texto a secas no lo hace aparecer.
            requiereTarget: true,
            fallbackSinTarget:
              'Todavía no tenés un target concreto. Buscá una palabra o frase que hayas fallado esta semana; si no se te ocurre ninguna, mirá el Learning Bank antes de abrir la herramienta.',
          },
          areas: ['habla_espontanea'],
        },
        {
          clave: 'd5-transferencia',
          titulo: 'Prueba en contexto nuevo',
          minutosSugeridos: 5,
          herramienta: 'chatgpt',
          prompt: 'd5',
          objetivo: 'Ver si la mejora sobrevive a un contexto distinto.',
          porQue:
            'La transferencia importa más que el rendimiento dentro del ejercicio. Si solo funciona ahí, no sirve.',
          instrucciones: [
            'Pedí una situación diferente de la que practicaste.',
            'Comprobá si mantenés la mejora sin pensar en ella.',
          ],
          uso: {
            queHacer:
              'En ChatGPT Voice, pedí una situación nueva y comprobá si la mejora se sostiene cuando no estás concentrado en ella.',
          },
          areas: ['habla_espontanea', 'naturalidad'],
        },
      ],
    },

    50: {
      duracion: 50,
      pasos: [
        {
          clave: 'd5-foco',
          titulo: 'Confirmar el foco de hoy',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          objetivo: 'Seleccionar el mayor limitante de la semana.',
          eligeFoco: true,
          porQue:
            'El baseline, los registros y las autoevaluaciones guían la dosis. La sugerencia es transparente: siempre ves de dónde sale.',
          instrucciones: [
            'Leé el foco sugerido y sus razones.',
            'Aceptalo o elegí otra área.',
            'Una prioridad, no cinco.',
          ],
        },
        {
          clave: 'd5-bloque-profundo',
          titulo: 'Bloque profundo',
          minutosSugeridos: 35,
          herramienta: 'segunFoco',
          prompt: 'd5',
          grabadora: true,
          objetivo: 'Atacar deliberadamente la dificultad principal.',
          porQue:
            'Más volumen solo tiene valor si está focalizado y activo. Treinta y cinco minutos dispersos no equivalen a treinta y cinco dirigidos.',
          instrucciones: [
            'Listening o naturalidad: YouGlish con un target concreto, más resumen y producción propia.',
            'Pronunciación: percepción con Accent Coach, grabación y transferencia.',
            'Habla espontánea, recuperación, gramática o interacción: ChatGPT Voice con presión comunicativa.',
            'No conviertas el bloque en un drill: tiene que terminar en producción.',
          ],
          uso: {
            queHacer:
              'Abrí solo la herramienta de tu foco y dedicale el bloque entero a esa área, cerrando con producción propia.',
            // Sin esto la interfaz NUNCA pinta el fallback de YouGlish:
            // declarar el texto a secas no lo hace aparecer.
            requiereTarget: true,
            fallbackSinTarget:
              'Todavía no tenés un target concreto. Buscá una palabra o frase que hayas fallado esta semana; si no se te ocurre ninguna, mirá el Learning Bank antes de abrir la herramienta.',
          },
          areas: ['habla_espontanea'],
        },
        {
          clave: 'd5-transferencia',
          titulo: 'Transferencia',
          minutosSugeridos: 10,
          herramienta: 'chatgpt',
          prompt: 'd5',
          objetivo: 'Probarlo en habla espontánea.',
          porQue: 'El ejercicio termina cuando podés usar la mejora hablando, no antes.',
          instrucciones: [
            'Copiá el prompt si hace falta y entrá a Voice.',
            'Usá una situación que no hayas practicado.',
            'No avises de qué estuviste trabajando: dejá que aparezca solo.',
          ],
          uso: {
            queHacer:
              'En ChatGPT Voice, conversá en una situación nueva y comprobá si lo trabajado aparece sin que lo fuerces.',
          },
          areas: ['habla_espontanea', 'naturalidad'],
        },
      ],
    },
  },
};
